/**
 * Module « Mon compteur d'eau » : lectures du compteur intelligent, conseils
 * d'économie, alertes et facturation. Données de démonstration.
 */

export interface MeterAddress {
  id: string
  street: string
  city: string
  active: boolean
  meterSerial: string
}

export const meterAddresses: MeterAddress[] = [
  {
    id: 'principale',
    street: '112, route 138',
    city: 'Baie-Trinité',
    active: true,
    meterSerial: 'CPT-4821-BT',
  },
  {
    id: 'chalet',
    street: '4, chemin du Phare',
    city: 'Pointe-des-Monts',
    active: false,
    meterSerial: 'CPT-1907-PM',
  },
]

export const meter = {
  lastReading: 'Aujourd’hui, 6 h 10',
  installedOn: 'Installé en juin 2024',
}

export type RangeId = 'semaine' | 'mois' | 'douze-mois'

export interface RangeSeries {
  id: RangeId
  label: string
  unit: 'L' | 'm³'
  /** Repère de comparaison : moyenne des résidences semblables. */
  benchmark: number
  points: { label: string; value: number }[]
}

export const ranges: Record<RangeId, RangeSeries> = {
  semaine: {
    id: 'semaine',
    label: 'Semaine',
    unit: 'L',
    benchmark: 340,
    points: [
      { label: 'Lun', value: 286 },
      { label: 'Mar', value: 312 },
      { label: 'Mer', value: 268 },
      { label: 'Jeu', value: 402 },
      { label: 'Ven', value: 355 },
      { label: 'Sam', value: 488 },
      { label: 'Dim', value: 312 },
    ],
  },
  mois: {
    id: 'mois',
    label: 'Mois',
    unit: 'm³',
    benchmark: 2.6,
    points: [
      { label: 'S. 1', value: 2.4 },
      { label: 'S. 2', value: 2.1 },
      { label: 'S. 3', value: 1.8 },
      { label: 'S. 4', value: 1.1 },
    ],
  },
  'douze-mois': {
    id: 'douze-mois',
    label: '12 mois',
    unit: 'm³',
    benchmark: 9.4,
    points: [
      { label: 'O', value: 8.2 },
      { label: 'N', value: 7.9 },
      { label: 'D', value: 9.6 },
      { label: 'J', value: 10.4 },
      { label: 'F', value: 9.8 },
      { label: 'M', value: 9.1 },
      { label: 'A', value: 8.6 },
      { label: 'M', value: 8.9 },
      { label: 'J', value: 10.2 },
      { label: 'J', value: 11.8 },
      { label: 'A', value: 11.1 },
      { label: 'S', value: 7.4 },
    ],
  },
}

export const rangeOrder: RangeId[] = ['semaine', 'mois', 'douze-mois']

export const summary = {
  todayLitres: 312,
  todayCost: '0,74 $',
  monthCubic: 7.4,
  monthChange: -8.2,
  forecastAmount: '95,97 $',
  forecastCubic: '12,3 m³',
}

export interface WaterInsight {
  id: string
  tone: 'attention' | 'positif'
  title: string
  detail: string
  action: 'fuite' | 'conseils'
}

/** Analyse prioritaire du compteur, calculée à partir du débit de nuit. */
export const priorityInsight: WaterInsight = {
  id: 'debit-nuit',
  tone: 'attention',
  title: 'Un débit continu de 4 L/h a été mesuré la nuit dernière, entre 1 h et 5 h',
  detail:
    'Quand personne n’utilise l’eau, le compteur devrait rester à zéro. Un débit constant indique souvent une toilette qui fuit — environ 96 L par jour, soit près de 2,9 m³ par mois.',
  action: 'fuite',
}

export interface WaterTip {
  id: string
  title: string
  detail: string
  saving: string
}

export const tips: WaterTip[] = [
  {
    id: 'toilette',
    title: 'Vérifier la toilette avec du colorant',
    detail:
      'Versez quelques gouttes de colorant dans le réservoir. Si la couleur apparaît dans la cuvette sans tirer la chasse, le clapet fuit.',
    saving: 'jusqu’à 2,9 m³ / mois',
  },
  {
    id: 'pommeau',
    title: 'Installer un pommeau de douche à faible débit',
    detail: 'Un pommeau à 6 L/min remplace un modèle à 12 L/min sans perte de confort.',
    saving: '≈ 1,4 m³ / mois',
  },
  {
    id: 'gel',
    title: 'Arrêter le filet d’eau antigel dès le dégel',
    detail:
      'Le filet laissé ouvert tout l’hiver représente la plus grosse dépense d’eau des résidences de la Côte-Nord.',
    saving: '≈ 8 m³ / mois',
  },
  {
    id: 'lave-vaisselle',
    title: 'Faire tourner les appareils à pleine charge',
    detail: 'Lave-vaisselle et laveuse consomment la même eau, pleins ou à moitié vides.',
    saving: '≈ 0,8 m³ / mois',
  },
]

export const alertChannels = [
  { id: 'courriel', label: 'Courriel' },
  { id: 'sms', label: 'Message texte' },
  { id: 'push', label: 'Notification mobile' },
]

export const alertTypes = [
  {
    id: 'fuite',
    label: 'Fuite probable',
    hint: 'Débit continu pendant plus de 3 heures',
  },
  {
    id: 'seuil',
    label: 'Dépassement du seuil mensuel',
    hint: 'Quand la consommation dépasse la cible',
  },
  {
    id: 'facture',
    label: 'Nouvelle facture d’eau',
    hint: 'Dès l’émission et 5 jours avant l’échéance',
  },
  {
    id: 'gel',
    label: 'Risque de gel',
    hint: 'Avis municipal de protection des conduites',
  },
]

export const thresholds = ['8 m³ par mois', '10 m³ par mois', '12 m³ par mois', '15 m³ par mois']

export const bill = {
  reference: '#EAU-2026-0418',
  period: 'Juillet à septembre 2026',
  consumption: '28,6 m³',
  amount: '95,97 $',
  dueOn: '15 octobre 2026',
  status: 'À payer' as const,
}

/** Carte enregistrée au dossier, proposée par défaut au paiement en ligne. */
export const savedCard = { brand: 'Visa', last4: '4127', expiry: '09/29' }

export const quickPrompts = [
  'Ma consommation moyenne',
  'Comment détecter les fuites',
  'Configurer une alerte',
  'Payer ma facture d’eau',
]
